/**
 * Milkdown Footnote Plugin
 *
 * Provides commands for inserting and managing footnotes with automatic numbering.
 *
 * Features:
 * - Auto-numbering: Automatically assigns numbers based on document order
 * - Smart insertion: Inserts reference at cursor, definition at end of document
 * - Auto-renumbering: Renumbers all footnotes after each insertion
 * - Auto-reordering: Reorders definitions to match reference order
 */

import { $command, $ctx } from '@milkdown/utils';
import type { Ctx } from '@milkdown/ctx';
import { Selection } from '@milkdown/prose/state';
import type { EditorState, Transaction } from '@milkdown/prose/state';
import type { Node as ProseMirrorNode, NodeType } from '@milkdown/prose/model';
import { footnoteReferenceSchema, footnoteDefinitionSchema } from '@milkdown/preset-gfm';
import { paragraphSchema } from '@milkdown/preset-commonmark';

/**
 * Footnote reference info
 */
interface FootnoteReference {
  pos: number;
  label: string;
  node: ProseMirrorNode;
}

/**
 * Footnote definition info
 */
interface FootnoteDefinition {
  pos: number;
  label: string;
  node: ProseMirrorNode;
}

/**
 * Footnote info from document
 */
interface FootnoteInfo {
  references: FootnoteReference[];
  definitions: FootnoteDefinition[];
}

/**
 * Footnote state context value
 */
interface FootnoteState {
  lastInsertedLabel: string | null;
}

// Use the Ctx type from @milkdown/ctx for proper typing

/**
 * Count existing footnotes in the document and find all their labels
 */
function getFootnoteInfo(doc: ProseMirrorNode): FootnoteInfo {
  const references: FootnoteReference[] = [];
  const definitions: FootnoteDefinition[] = [];

  doc.descendants((node: ProseMirrorNode, pos: number) => {
    if (node.type.name === 'footnote_reference') {
      references.push({
        pos,
        label: node.attrs.label as string,
        node
      });
    }
    if (node.type.name === 'footnote_definition') {
      definitions.push({
        pos,
        label: node.attrs.label as string,
        node
      });
    }
  });

  return { references, definitions };
}

/**
 * Generate a unique temporary label that won't conflict with existing ones
 */
function generateTempLabel(doc: ProseMirrorNode): string {
  const { references, definitions } = getFootnoteInfo(doc);
  const existingLabels = new Set([
    ...references.map(r => r.label),
    ...definitions.map(d => d.label)
  ]);

  // Use a UUID-like prefix to avoid conflicts
  let tempLabel = `__temp_${Date.now()}`;
  while (existingLabels.has(tempLabel)) {
    tempLabel = `__temp_${Date.now()}_${Math.random()}`;
  }
  return tempLabel;
}

/**
 * Renumber and reorder all footnotes in a transaction based on reference order
 * Returns the new transaction with renumbered and reordered footnotes
 */
function renumberFootnotesInTransaction(ctx: Ctx, tr: Transaction): Transaction {
  let doc = tr.doc;
  let { references, definitions } = getFootnoteInfo(doc);

  if (references.length === 0) {
    return tr;
  }

  // Sort references by position (order of appearance in document)
  const sortedRefs = [...references].sort((a, b) => a.pos - b.pos);

  // Create a mapping from old labels to new labels (1, 2, 3, ...)
  const labelMapping = new Map<string, string>();
  sortedRefs.forEach((ref, index) => {
    const newLabel = String(index + 1);
    labelMapping.set(ref.label, newLabel);
  });

  // Get node types
  const refType = footnoteReferenceSchema.type(ctx) as NodeType;
  const defType = footnoteDefinitionSchema.type(ctx) as NodeType;

  let newTr = tr;

  // Step 1: Update all reference labels (from end to start to preserve positions)
  const sortedRefsReverse = [...references].sort((a, b) => b.pos - a.pos);
  sortedRefsReverse.forEach(ref => {
    const newLabel = labelMapping.get(ref.label);
    if (newLabel && ref.label !== newLabel) {
      const newNode = refType.create({ label: newLabel });
      newTr = newTr.replaceWith(ref.pos, ref.pos + ref.node.nodeSize, newNode);
    }
  });

  // Step 2: Collect definition contents by their OLD label, then delete all definitions
  // We need to re-read the doc after the reference updates
  doc = newTr.doc;
  ({ definitions } = getFootnoteInfo(doc));

  // Map old label -> definition content (the node's content)
  const defContentByOldLabel = new Map<string, ProseMirrorNode>();

  // Find original labels before our renumbering (use inverse mapping)
  const inverseLabelMapping = new Map<string, string>();
  labelMapping.forEach((newLabel, oldLabel) => {
    inverseLabelMapping.set(newLabel, oldLabel);
  });

  definitions.forEach(def => {
    // The def.label might already be updated or not, we need to track by content
    defContentByOldLabel.set(def.label, def.node);
  });

  // Sort definitions by position (end to start) and delete them
  const sortedDefsReverse = [...definitions].sort((a, b) => b.pos - a.pos);
  sortedDefsReverse.forEach(def => {
    newTr = newTr.delete(def.pos, def.pos + def.node.nodeSize);
  });

  // Step 3: Re-insert definitions in the correct order (1, 2, 3, ...)
  // Find end of document after deletions
  let insertPos = newTr.doc.content.size;

  // Insert definitions in order 1, 2, 3...
  for (let i = 1; i <= sortedRefs.length; i++) {
    const newLabel = String(i);
    const oldLabel = inverseLabelMapping.get(newLabel) || newLabel;

    // Find the definition content for this label
    const defNode = defContentByOldLabel.get(oldLabel) || defContentByOldLabel.get(newLabel);

    if (defNode) {
      // Create new definition with correct label
      const newDefNode = defType.create({ label: newLabel }, defNode.content);
      newTr = newTr.insert(insertPos, newDefNode);
      insertPos = newTr.doc.content.size;
    }
  }

  return newTr;
}

/**
 * Command to insert a new footnote at the cursor position.
 * Automatically numbers the footnote and renumbers all existing footnotes.
 */
export const insertFootnoteCommand = $command(
  'InsertFootnote',
  (ctx) => (noteText?: string) => (state: EditorState, dispatch?: (tr: Transaction) => void) => {
    const { selection, tr, doc } = state;
    const { from } = selection;

    // Generate a temporary unique label
    const tempLabel = generateTempLabel(doc);

    // Get node types from context
    const refType = footnoteReferenceSchema.type(ctx) as NodeType;
    const defType = footnoteDefinitionSchema.type(ctx) as NodeType;
    const paragraphType = paragraphSchema.type(ctx) as NodeType;

    // Create the footnote reference node with temp label
    const refNode = refType.create({ label: tempLabel });

    // Insert the reference at cursor position using replaceSelection to avoid <br>
    let newTr = tr.replaceSelectionWith(refNode, false);

    // Find the end of the document for the definition
    const docEnd = newTr.doc.content.size;

    // Create the footnote definition with paragraph content
    const defContent = paragraphType.create(
      null,
      noteText ? state.schema.text(noteText) : null
    );
    const defNode = defType.create({ label: tempLabel }, defContent);

    // Insert the definition at the end of the document
    newTr = newTr.insert(docEnd, defNode);

    // Now renumber and reorder all footnotes
    newTr = renumberFootnotesInTransaction(ctx as Ctx, newTr);

    // Move cursor after the inserted reference
    const newSelection = Selection.near(newTr.doc.resolve(from + 1));
    newTr = newTr.setSelection(newSelection);

    if (dispatch) {
      dispatch(newTr.scrollIntoView());
    }

    return true;
  }
);

/**
 * Command to manually renumber all footnotes in the document.
 * Renumbers based on the order of references in the document.
 */
export const renumberFootnotesCommand = $command(
  'RenumberFootnotes',
  (ctx) => () => (state: EditorState, dispatch?: (tr: Transaction) => void) => {
    const { tr } = state;

    const newTr = renumberFootnotesInTransaction(ctx as Ctx, tr);

    if (dispatch && newTr.docChanged) {
      dispatch(newTr);
    }

    return newTr.docChanged;
  }
);

/**
 * Context slice to track footnote state (optional, for future use)
 */
export const footnoteStateCtx = $ctx<FootnoteState, 'footnoteState'>(
  { lastInsertedLabel: null },
  'footnoteState'
);

/**
 * The footnote plugin that registers all commands
 */
export const footnotePlugin = [
  insertFootnoteCommand,
  renumberFootnotesCommand,
  footnoteStateCtx,
];

export default footnotePlugin;
